'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@algoacademy/design-system/web/overlays/Dialog';
import { P2 } from '@algoacademy/design-system/web/typography/Paragraph/P2';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { type ReactNode, useState } from 'react';
import { IconOnTheLeftWithContentLayout } from '@/app/(marketing-site)/mentoring/_layouts/IconOnTheLeftWithContentLayout';
export type IncludedPlanBenefit = {
  lead: string;
  rest: ReactNode;
};
export type IncludedPlanBenefitSection = {
  heading?: string;
  benefits: IncludedPlanBenefit[];
};

/** Benefits that conflict when nested under a higher plan (different limits / guarantee). */
const EXCLUDED_FROM_INCLUDED_PLAN_MODAL = new Set(['Gwarancja zwrotu pieniędzy', 'Gwarancja zwrotu', 'Maks. 5 uczniów na mentora']);
const EXCLUDED_FROM_PROJECT_INCLUDED_PLAN_MODAL = new Set([...EXCLUDED_FROM_INCLUDED_PLAN_MODAL, 'Narzędzia AI w cenie', 'Regularne spotkania 1:1 z mentorem', 'Własne projekty']);
export function benefitsForIncludedPlanModal(benefits: IncludedPlanBenefit[], options?: {
  forProjectPlan?: boolean;
}): IncludedPlanBenefit[] {
  const excluded = options?.forProjectPlan ? EXCLUDED_FROM_PROJECT_INCLUDED_PLAN_MODAL : EXCLUDED_FROM_INCLUDED_PLAN_MODAL;
  return benefits.filter(benefit => !excluded.has(benefit.lead));
}
type IncludedPlanBenefitsDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  sections: IncludedPlanBenefitSection[];
};
export function IncludedPlanBenefitsDialog(t0) {
  const $ = _c(23);
  const {
    open,
    onClose,
    title,
    sections
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <DialogTitle>{title}</DialogTitle>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <XMarkIcon className="size-5" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose) {
    t3 = <Button plain={true} type="button" onClick={onClose} aria-label="Zamknij">{t2}</Button>;
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <div className="flex items-start justify-between gap-4">{t1}{t3}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== sections || $[9] !== title) {
    let t6;
    if ($[11] !== title) {
      t6 = section => <div key={section.heading ?? title} className="space-y-4">{section.heading ? <P2 className="font-semibold text-slate-900">{section.heading}</P2> : null}<ul className="space-y-4">{section.benefits.map(_temp)}</ul></div>;
      $[11] = title;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = sections.map(t6);
    $[8] = sections;
    $[9] = title;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <DialogBody className="max-h-[70vh] overflow-y-auto"><div className="space-y-6">{t5}</div></DialogBody>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onClose) {
    t7 = <DialogActions><Button outline={true} type="button" onClick={onClose}>Zamknij</Button></DialogActions>;
    $[15] = onClose;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== onClose || $[18] !== open || $[19] !== t4 || $[20] !== t6 || $[21] !== t7) {
    t8 = <Dialog open={open} onClose={onClose} className="max-w-2xl">{t4}{t6}{t7}</Dialog>;
    $[17] = onClose;
    $[18] = open;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function _temp(t0) {
  const {
    lead,
    rest
  } = t0;
  return <li key={lead}><IconOnTheLeftWithContentLayout mainContent={<P2 className="text-contentSecondary"><strong className="block font-semibold text-slate-900">{lead}</strong>{rest}</P2>} /></li>;
}
type IncludedPlanBenefitItemProps = {
  lead: string;
  rest: string;
  dialogTitle: string;
  sections: IncludedPlanBenefitSection[];
};
export function IncludedPlanBenefitItem(t0) {
  const $ = _c(14);
  const {
    lead,
    rest,
    dialogTitle,
    sections
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== lead) {
    t1 = <strong className="block font-semibold text-contentPrimary">{lead}</strong>;
    $[0] = lead;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <button type="button" onClick={() => setOpen(true)} className="font-semibold text-algoprimary underline">Zobacz, co zawiera</button>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== rest || $[4] !== t1) {
    t3 = <IconOnTheLeftWithContentLayout mainContent={<P2 className="text-contentSecondary">{t1}{rest}{" "}{t2}</P2>} />;
    $[3] = rest;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpen(false);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== dialogTitle || $[8] !== open || $[9] !== sections) {
    t5 = <IncludedPlanBenefitsDialog open={open} onClose={t4} title={dialogTitle} sections={sections} />;
    $[7] = dialogTitle;
    $[8] = open;
    $[9] = sections;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <>{t3}{t5}</>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}